import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";
import styles from "./LineChart.module.css";

Chart.register(...registerables);

const SERIES_SLOTS = 5;

const LineChart = ({
  title = "Line Chart",
  icon,
  showInfoIcon = false,
  onInfoClick,
  infoTitle,
  infoContent,
  labels = [],
  datasets = [],
  filterOptions = [],
  activeFilter,
  onFilterChange,
  showFilters = true,
  loading = false,
  error = null,
  emptyMessage = "No data available",
  height = 320,
  tension = 0,
  yMin,
  yMax,
  yStepSize,
  /* Suffix appended to each y-axis tick number, e.g. "%" → "100%", "90%". */
  yTickSuffix = "",
  threshold = null,
  thresholdColor = "#ef4444",
  thresholdLabel = "Threshold",
  thresholdDashed = false,
  /* When true, the threshold line gets its own (red square) legend entry
   * at the bottom of the chart. It stays hidden from the tooltip regardless. */
  showThresholdLegend = false,
  pointDetails = null,
  onPointClick,
  tooltipFormatter = null,
  drilldownLoading = false,
  drilldownError = null,
  drilldownColumns = null,
  /* Suffix appended to the drilldown table title, e.g. " — Remedial Details" */
  drilldownTitleSuffix = "",
  /* Optional axis titles */
  xAxisLabel = null,
  yAxisLabel = null,
  note = null,
  /* Extra node rendered in the header, stacked above the note (e.g. a dropdown). */
  selectControl = null,
  drilldownPerPage = 10,
  drilldownText = {},
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [showInfo, setShowInfo] = useState(false);

  /* ── Drilldown state ── */
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [page, setPage] = useState(1);

  const hasData =
    Array.isArray(labels) &&
    labels.length > 0 &&
    Array.isArray(datasets) &&
    datasets.length > 0;

  const drilldownEnabled =
    Array.isArray(pointDetails) && pointDetails.length > 0;
  const inTable = drilldownEnabled && selectedIndex !== null;

  const dt = {
    studentName: "Student Name",
    assessmentCount: "No. of Assessment",
    status: "Status",
    showing: "Showing",
    to: "to",
    of: "of",
    entries: "entries",
    previous: "Previous",
    next: "Next",
    pass: "Pass",
    fail: "Fail",
    ...drilldownText,
  };

  /* Default table columns (preserves the original layout when no custom
   * columns are supplied). */
  const defaultColumns = [
    { key: "name", label: dt.studentName },
    { key: "assessmentCount", label: dt.assessmentCount },
    {
      key: "status",
      label: dt.status,
      align: "center",
      render: (s) =>
        s.status === "pass" ? (
          <i
            className={`fa-solid fa-circle-check ${styles.lineStatusPass}`}
            title={dt.pass}
          />
        ) : (
          <i
            className={`fa-solid fa-circle-xmark ${styles.lineStatusFail}`}
            title={dt.fail}
          />
        ),
    },
  ];

  const columns =
    Array.isArray(drilldownColumns) && drilldownColumns.length > 0
      ? drilldownColumns
      : defaultColumns;

  useEffect(() => {
    // No chart while the table is open, or when there's nothing to draw.
    if (!canvasRef.current || !hasData || loading || error || inTable) {
      chartRef.current?.destroy();
      chartRef.current = null;
      return;
    }

    const ctx = canvasRef.current.getContext("2d");
    const cardStyle = getComputedStyle(canvasRef.current);
    const getToken = (name) => cardStyle.getPropertyValue(name).trim();

    const chartDatasets = datasets.map((ds, index) => {
      const slot = (index % SERIES_SLOTS) + 1;
      const color = ds.color || getToken(`--line-series-${slot}-color`);
      const POINT_ABOVE = "#22c55e"; // green — at/above threshold (peak)
      const POINT_BELOW = "#ef4444"; // red — below threshold (low)

      const pointColor = (ctx) => {
        const v = ctx.parsed?.y;
        if (!Number.isFinite(threshold) || v == null) return color;
        return v >= threshold ? POINT_ABOVE : POINT_BELOW;
      };
      return {
        label: ds.label,
        data: ds.data,
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: pointColor,
        pointBorderColor: pointColor,
        pointHoverBackgroundColor: pointColor,
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 2,
        pointRadius: ds.pointRadius !== undefined ? ds.pointRadius : 4,
        pointHoverRadius:
          ds.pointHoverRadius !== undefined ? ds.pointHoverRadius : 6,
        borderWidth: ds.borderWidth !== undefined ? ds.borderWidth : 2.5,
        borderDash: ds.borderDash !== undefined ? ds.borderDash : [],
        tension,
        fill: false,
        _hideFromLegend: !!ds.hideFromLegend,
        _hideFromTooltip: !!ds.hideFromLegend,
      };
    });

    if (Number.isFinite(threshold) && labels.length > 0) {
      chartDatasets.push({
        label: thresholdLabel,
        data: labels.map(() => threshold),
        borderColor: thresholdColor,
        backgroundColor: thresholdColor,
        borderWidth: 1.5,
        borderDash: thresholdDashed ? [6, 6] : [],
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
        // Show as a red square in the legend when requested, but never in the tooltip.
        _hideFromLegend: !showThresholdLegend,
        _hideFromTooltip: true,
      });
    }

    chartRef.current?.destroy();

    chartRef.current = new Chart(ctx, {
      type: "line",
      data: { labels, datasets: chartDatasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: "index", intersect: false },
        layout: { padding: { right: 12, top: 8, bottom: 8, left: 4 } },
        // Pointer cursor + click-to-drilldown (only when enabled)
        onHover: drilldownEnabled
          ? (e, els) => {
              if (e?.native?.target) {
                e.native.target.style.cursor = els.length
                  ? "pointer"
                  : "default";
              }
            }
          : undefined,
        onClick: drilldownEnabled
          ? (evt, _els, chart) => {
              const pts = chart.getElementsAtEventForMode(
                evt,
                "nearest",
                { intersect: true },
                true,
              );
              const real = pts.find((p) => p.datasetIndex === 0);
              if (real) {
                setSelectedIndex(real.index);
                setPage(1);
                // Let the parent lazily fetch the drilldown rows for this point.
                onPointClick?.(real.index);
              }
            }
          : undefined,
        plugins: {
          legend: {
            position: "bottom",
            align: "center",
            labels: {
              usePointStyle: true,
              pointStyle: "rect",
              boxWidth: 20,
              boxHeight: 16,
              padding: 20,
              font: { size: 13, weight: "500" },
              color: "#4b5563",
              generateLabels: (chart) =>
                chart.data.datasets
                  .map((ds, i) => ({ ds, i }))
                  .filter(({ ds }) => !ds._hideFromLegend)
                  .map(({ ds, i }) => {
                    const swatch =
                      typeof ds.borderColor === "string"
                        ? ds.borderColor
                        : "#38bdf8";
                    return {
                      text: ds.label,
                      fillStyle: swatch,
                      strokeStyle: swatch,
                      lineWidth: 0,
                      pointStyle: "rect",
                      hidden: !chart.isDatasetVisible(i),
                      datasetIndex: i,
                    };
                  }),
            },
          },
          tooltip: {
            backgroundColor: "rgba(17, 24, 39, 0.95)",
            padding: 12,
            cornerRadius: 8,
            titleFont: { weight: "600", size: 13 },
            bodyFont: { size: 12 },
            boxPadding: 4,
            usePointStyle: true,
            filter: (item) =>
              !chartDatasets[item.datasetIndex]._hideFromTooltip,
            callbacks: {
              title: (items) => labels[items[0].dataIndex],
              label: (item) => {
                // 1) Custom formatter wins
                if (typeof tooltipFormatter === "function") {
                  const out = tooltipFormatter(
                    item.dataIndex,
                    item.datasetIndex,
                  );
                  if (out == null) return undefined;
                  return Array.isArray(out) ? out : [out];
                }
                // 2) Drilldown details (Assessment Trend behavior)
                if (drilldownEnabled && item.datasetIndex === 0) {
                  const d = pointDetails[item.dataIndex];
                  if (!d) return item.formattedValue;
                  return [
                    `Total student: ${d.total}`,
                    `${dt.pass}: ${d.pass}`,
                    `${dt.fail}: ${d.fail}`,
                  ];
                }
                // 3) Default — Chart.js's auto label
                return undefined;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            ticks: { display: false },
            border: { display: false },
            title: {
              display: !!xAxisLabel,
              text: xAxisLabel || "",
              color: "#6b7280",
              font: { size: 13, weight: "600" },
              padding: { top: 6 },
            },
          },
          y: {
            min: yMin,
            max: yMax,
            grid: { color: "rgba(0, 0, 0, 0.06)", drawBorder: false },
            ticks: {
              color: "#9ca3af",
              font: { size: 12 },
              ...(yStepSize !== undefined ? { stepSize: yStepSize } : {}),
              ...(yTickSuffix
                ? { callback: (value) => `${value}${yTickSuffix}` }
                : {}),
            },
            border: { display: false },
            title: {
              display: !!yAxisLabel,
              text: yAxisLabel || "",
              color: "#6b7280",
              font: { size: 13, weight: "600" },
              padding: { bottom: 6 },
            },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [
    labels,
    datasets,
    hasData,
    loading,
    error,
    tension,
    yMin,
    yMax,
    yStepSize,
    yTickSuffix,
    threshold,
    thresholdColor,
    thresholdLabel,
    thresholdDashed,
    showThresholdLegend,
    drilldownEnabled,
    pointDetails,
    inTable,
    onPointClick,
    xAxisLabel,
    yAxisLabel,
    tooltipFormatter,
  ]);

  /* Close any open drilldown when the underlying chart data changes
   * (e.g. the parent switches subject) so a stale table isn't shown.
   * Keyed on the label content so normal re-renders don't reset it. */
  const labelsKey = labels.join("|");
  useEffect(() => {
    setSelectedIndex(null);
    setPage(1);
  }, [labelsKey]);

  const normalizedFilters = filterOptions.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt,
  );

  const handleInfoClick = () => {
    setShowInfo(true);
    onInfoClick?.();
  };

  /* ── Derived table data ── */
  const detail = inTable ? pointDetails[selectedIndex] : null;
  const students = Array.isArray(detail?.students) ? detail.students : [];
  const totalPages = Math.max(1, Math.ceil(students.length / drilldownPerPage));
  const start = (page - 1) * drilldownPerPage;
  const pageItems = students.slice(start, start + drilldownPerPage);
  const displayTitle =
    inTable && labels[selectedIndex]
      ? `${title} — ${labels[selectedIndex]}${drilldownTitleSuffix || ""}`
      : title;

  return (
    <div className={styles.lineCard}>
      <div className={styles.lineHeader}>
        <div className={styles.lineTitle}>
          {icon && <span className={styles.lineIconWrap}>{icon}</span>}
          <h3>{displayTitle}</h3>
          {showInfoIcon && (
            <button
              type="button"
              className={styles.lineInfoBtn}
              onClick={handleInfoClick}
              aria-label="More information"
            >
              <i className="fa-solid fa-circle-info" />
            </button>
          )}
        </div>

        <div className={styles.lineHeaderRight}>
          {inTable ? (
            <button
              type="button"
              className={styles.lineCloseBtn}
              onClick={() => setSelectedIndex(null)}
              aria-label="Back to chart"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          ) : (
            <>
              {showFilters && normalizedFilters.length > 0 && (
                <div className={styles.lineFilters} role="tablist">
                  {normalizedFilters.map((opt) => {
                    const isActive = activeFilter === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`${styles.lineFilterBtn} ${
                          isActive ? styles.lineFilterActive : ""
                        }`}
                        onClick={() => onFilterChange?.(opt.value)}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Subject dropdown (top) + note (bottom) stacked in a column */}
              {(selectControl || note) && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 8,
                    minWidth: 260,
                    maxWidth: 380,
                  }}
                >
                  {selectControl}
                  {note && (
                    <div
                      className={styles.lineNoteBox}
                      style={{ maxWidth: "100%" }}
                    >
                      <strong>{dt.note || "Note:"}</strong> {note}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ===== Table view ===== */}
      {inTable ? (
        <div className={styles.lineTableWrap}>
          {drilldownLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: height,
              }}
            >
              <div className={styles.lineSpinner} />
            </div>
          ) : drilldownError ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: height,
                color: "var(--text-muted, #6b7280)",
                fontSize: 14,
                textAlign: "center",
                padding: 16,
              }}
            >
              {drilldownError}
            </div>
          ) : students.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: height,
                color: "var(--text-muted, #6b7280)",
                fontSize: 14,
                textAlign: "center",
                padding: 16,
              }}
            >
              {emptyMessage}
            </div>
          ) : (
            <>
              <div className={styles.lineTableScroll}>
                <table className={styles.lineTable}>
                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th
                          key={c.key}
                          className={`${styles.lineTh} ${
                            c.align === "center" ? styles.lineCenter : ""
                          }`}
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((s) => (
                      <tr key={s.id} className={styles.lineRow}>
                        {columns.map((c) => (
                          <td
                            key={c.key}
                            className={`${styles.lineTd} ${
                              c.align === "center" ? styles.lineCenter : ""
                            }`}
                          >
                            {c.render ? c.render(s) : s[c.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.linePagination}>
                <span className={styles.lineEntries}>
                  {dt.showing} {students.length === 0 ? 0 : start + 1} {dt.to}{" "}
                  {Math.min(start + drilldownPerPage, students.length)} {dt.of}{" "}
                  {students.length} {dt.entries}
                </span>
                <div className={styles.linePageBtns}>
                  <button
                    type="button"
                    className={styles.linePageBtn}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {dt.previous}
                  </button>
                  <button
                    type="button"
                    className={styles.linePageBtn}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {dt.next}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        /* ===== Chart view ===== */
        <div className={styles.lineChart} style={{ height }}>
          {loading && (
            <div className={styles.lineState}>
              <div className={styles.lineSpinner} />
            </div>
          )}
          {!loading && error && (
            <div className={`${styles.lineState} ${styles.lineError}`}>
              {error}
            </div>
          )}
          {!loading && !error && !hasData && (
            <div className={styles.lineState}>{emptyMessage}</div>
          )}
          {!loading && !error && hasData && <canvas ref={canvasRef} />}
        </div>
      )}

      {showInfo && infoContent && (
        <div
          onClick={() => setShowInfo(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--color-white, #fff)",
              padding: "24px 24px 20px",
              borderRadius: "12px",
              maxWidth: "480px",
              width: "100%",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() => setShowInfo(false)}
              aria-label="Close"
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "transparent",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
                color: "var(--text-secondary, #6b7280)",
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "12px",
              }}
            >
              <i
                className="fa-solid fa-circle-info"
                style={{
                  color: "var(--blue-light, #3b82f6)",
                  fontSize: "20px",
                }}
              />
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "var(--text-dark, #111827)",
                }}
              >
                {infoTitle || title}
              </h3>
            </div>
            <div
              style={{
                margin: 0,
                fontSize: "14px",
                lineHeight: 1.6,
                color: "var(--text-secondary, #4b5563)",
              }}
            >
              {typeof infoContent === "string" ? (
                <p style={{ margin: 0 }}>{infoContent}</p>
              ) : (
                infoContent
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineChart;
